<template>
  <div class="page__wrapper">
    <div class="container-static">
      <template
        v-for="block in blocks"
        :key="block.id"
      >
        <div
          v-if="block.type === 'image'"
          class="image__wrapper">
          <img
            v-if="block.src"
            :src="block.src"
            alt=""
            srcset=""
          >
        </div>
        <div
          v-if="block.type === 'html'"
          v-html="block.value"
          class="inner-html"
        ></div>
        <p
          v-if="block.type === 'text'"
          class="break-line"
        >
          {{ description }}
        </p>
      </template>
    </div>
  </div>
</template>
<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import StaticPage from '@/components/mixins/methodsStaticPage';

export default {
  name: 'StaticPageView',
  mixins: [appBaseNav, StaticPage],
  props: {
    alias: {
      type: [String],
      required: true,
    },
  },
  data() {
    return {
      blocks: [],
    };
  },
  watch: {
    alias() {
      this.loadPage();
    },
    $route(to) {
      if (to.hash) {
        const toElement = document.getElementById(to.hash.replace('#', ''));
        if (toElement) toElement.scrollIntoView();
      } else {
        document.getElementById('content__wrapper').scrollTo(0, 0);
      }
    },
  },
  beforeMount() {
    this.loadPage();
  },
  mounted() {
    if (this.$route.hash) {
      setTimeout(() => {
        const toElement = document.getElementById(this.$route.hash.replace('#', ''));
        if (toElement) toElement.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  },
  methods: {
    loadPage() {
      this.$emit('loading', true);
      this.getPage(this.alias)
        .then((content) => {
          this.$emit('loading', false);
          this.$store.commit('setPageTitle', content.title || null);
          this.blocks = [...content.blocks];
        })
        .catch(() => {});
    },
  },
};
</script>
<style lang="scss">
 @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
.container-static {
  font-family: 'Montserrat', sans-serif;
  color: #1a1a1a;
  background: #f5f5f5;
  line-height: 1.65;
  font-size: 15px;

  /* ─── Container ─── */
  .container {
    max-width: 760px;
    margin: 0 auto;
    padding: 0 20px 60px;
  }

  /* ─── TOC ─── */
  .toc {
    background: #fff;
    border-radius: 16px;
    margin: 28px 0;
    padding: 28px 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
  }
  .toc h2 {
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 16px;
    color: #1a1a1a;
  }
  .toc ol {
    list-style: none;
    counter-reset: toc-counter;
    padding: 0;
  }
  .toc ol li {
    counter-increment: toc-counter;
    margin-bottom: 0;
  }
  .toc ol li a {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    text-decoration: none;
    color: #1a1a1a;
    font-size: 14px;
    font-weight: 500;
    border-radius: 10px;
    transition: background .2s, color .2s;
  }
  .toc ol li a:hover {
    background: #FDF3E4;
    color: #2D3038;
  }
  .toc ol li a::before {
    content: counter(toc-counter);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #2D3038;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    margin-right: 14px;
    flex-shrink: 0;
  }

  /* ─── Section ─── */
  .section {
    background: #fff;
    border-radius: 16px;
    margin-top: 24px;
    padding: 32px 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    scroll-margin-top: 120px;
  }
  .section h2 {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 3px solid #FCB136;
    display: inline-block;
  }
  .section p {
    margin-bottom: 12px;
    color: #1a1a1a;
  }
  .section p:last-child {
    margin-bottom: 0;
  }
  .note {
    color: #888;
    font-size: 13px;
    font-style: italic;
    margin-top: 8px;
  }

  /* ─── Screenshots ─── */
  .screenshots {
    display: flex;
    gap: 16px;
    margin-top: 20px;
    justify-content: center;
    flex-wrap: wrap;
  }
  .screenshots img {
    width: 220px;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0,0,0,.12);
    border: 1px solid #eee;
    cursor: pointer;
    transition: transform .2s, box-shadow .2s;
  }
  .screenshots img:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 28px rgba(0,0,0,.18);
  }

  /* ─── Back to top ─── */
  .back-top {
    position: fixed;
    bottom: 28px;
    right: 28px;
    width: 48px;
    height: 48px;
    background: #2D3038;
    color: #fff;
    border: none;
    border-radius: 50%;
    font-size: 22px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(45,48,56,.25);
    display: none;
    align-items: center;
    justify-content: center;
    transition: transform .2s;
    z-index: 50;
  }
  .back-top:hover {
    transform: scale(1.1);
  }
  .back-top.visible {
    display: flex;
  }

  /* ─── Responsive ─── */
  @media (max-width: 520px) {
    .container { padding: 0 12px 40px; }
    .section, .toc { padding: 24px 18px; }
    .screenshots img { width: 160px; }
  }
}

.page {
  &__wrapper {
    padding: 24px 12px 0;
  }
}
.image__wrapper {
  display: flex;
  justify-content: center;
  overflow: auto;
  img {
    // max-height: calc(var(--vh, 1vh) * 100 - 166px);
    max-width: 100%;
    max-height: 100%;
  }
}

</style>
